/* =============================================================================
   assets/rm-save-my-ring.js
   Save My Ring — ATC email capture.

   Listens for the cart:itemAdded custom event dispatched by rm-cart.js,
   waits a configurable delay, then opens the panel (once per page load).
   On submit, POSTs the email + product context to Klaviyo's Subscribe v3 API.

   Config is set in snippets/save-my-ring.liquid via window._rmSMR:
     klaviyoKey  — public API key (Ln3LJD)
     klaviyoList — list ID (S9ZEj9)
     delay       — ms to wait after ATC before showing panel (default 2500)
   ============================================================================= */

(function () {
  'use strict';

  /* ── Config ── */
  const cfg = window._rmSMR || {};
  const KLAVIYO_KEY  = cfg.klaviyoKey  || '';
  const KLAVIYO_LIST = cfg.klaviyoList || '';
  const DELAY_MS     = cfg.delay       || 2500;

  /* ── Constants ── */
  const SMR_COOLDOWN_KEY  = 'rm_smr_cooldown';
  const SMR_COOLDOWN_DAYS = 30;

  /* ── State ── */
  let panelShownThisLoad = false;
  let pendingTimer       = null;
  let lastItem           = null; /* the item detail from cart:itemAdded */
  let lastImageUrl       = null; /* clean full-size image URL for Klaviyo */

  /* ── Cooldown helpers ── */
  function isOnCooldown() {
    /* Testing mode: ignore cooldown entirely when flag is set in theme settings */
    if (window._rmSMR?.ignoreCooldown) return false;
    try {
      var ts = localStorage.getItem(SMR_COOLDOWN_KEY);
      if (!ts) return false;
      var elapsed = Date.now() - parseInt(ts, 10);
      return elapsed < SMR_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
    } catch (e) { return false; }
  }

  function setCooldown() {
    try { localStorage.setItem(SMR_COOLDOWN_KEY, Date.now().toString()); } catch (e) {}
  }

  /* ── Product type gate ── */
  function isEligibleProductType(item) {
    if (!item) return false;
    var pt = (item.product_type || '').trim();
    return pt.indexOf('Ring-') === 0 || pt === 'cpb_ordered';
  }

  /* ── DOM refs ── */
  const overlay      = document.getElementById('rm-smr-overlay');
  const backdrop     = document.getElementById('rm-smr-backdrop');
  const panel        = document.getElementById('rm-smr-panel');
  const closeBtn     = document.getElementById('rm-smr-close');
  const formState    = document.getElementById('rm-smr-form-state');
  const successState = document.getElementById('rm-smr-success');
  const form         = document.getElementById('rm-smr-form');
  const emailInput   = document.getElementById('rm-smr-email');
  const submitBtn    = form?.querySelector('.rm-smr-submit');
  const errorEl      = document.getElementById('rm-smr-error');
  const successClose = document.getElementById('rm-smr-success-close');
  const ringCard     = document.getElementById('rm-smr-ring-card');
  const ringName     = document.getElementById('rm-smr-ring-name');
  const ringMeta     = document.getElementById('rm-smr-ring-meta');
  const ringImg      = overlay?.querySelector('.rm-smr-ring-img');

  if (!overlay) return; /* snippet not in DOM */

  /* ── Open / close ── */
  function openPanel() {
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    overlay.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    setTimeout(function () { emailInput?.focus(); }, 340);
  }

  function closePanel() {
    overlay.classList.remove('is-open');
    overlay.classList.remove('rm-smr-overlay--centered');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('inert', '');
    document.body.style.overflow = '';
    setCooldown();
  }

  /* ── Populate ring card from cart:itemAdded detail ── */
  function populateCard(item) {
    if (!item || !ringCard) return;

    var title    = item.product_title || item.title || '';
    var variant  = item.variant_title && item.variant_title !== 'Default Title'
                   ? item.variant_title : '';
    var price    = item.price ? formatMoney(item.price) : '';
    var imgSrc   = item.featured_image?.url || item.image || '';

    /* Store a clean full-size URL for Klaviyo (ensure protocol, strip query string) */
    if (imgSrc) {
      var cleanImg = imgSrc.indexOf('//') === 0 ? 'https:' + imgSrc : imgSrc;
      lastImageUrl = cleanImg.split('?')[0];
    }

    /* Resize via Shopify CDN: insert _120x120_crop_center before the file extension,
       preserving any existing query string (version param etc.) */
    if (imgSrc) {
      imgSrc = imgSrc.replace(
        /\.([a-zA-Z]{3,4})(\?.*)?$/,
        function (match, ext, qs) {
          return '_120x120_crop_center.' + ext + (qs || '');
        }
      );
      /* Ensure protocol */
      if (imgSrc.indexOf('//') === 0) imgSrc = 'https:' + imgSrc;
    }

    if (ringName) ringName.textContent = title;

    var metaParts = [];
    if (variant) metaParts.push(variant);
    if (price)   metaParts.push(price);
    if (ringMeta) ringMeta.textContent = metaParts.join(' · ');

    /* Product image */
    if (imgSrc && ringImg) {
      /* Remove the CSS ::after ring-hole effect when showing a real image */
      ringImg.classList.add('has-image');
      var img = document.createElement('img');
      img.src     = imgSrc;
      img.alt     = title;
      img.width   = 48;
      img.height  = 48;
      img.loading = 'lazy';
      /* Clear any existing image first */
      ringImg.querySelectorAll('img').forEach(function (el) { el.remove(); });
      ringImg.appendChild(img);
    }

    ringCard.removeAttribute('aria-hidden');
  }

  /* ── Currency formatter ── */
  function formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style:                 'currency',
      currency:              window.Shopify?.currency?.active || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  }

  /* ── Listen for ATC ── */
  document.addEventListener('cart:itemAdded', function (e) {
    /* Only show once per page load */
    if (panelShownThisLoad) return;

    /* Cooldown: don't show if already interacted within the cooldown window */
    if (isOnCooldown()) return;

    var item = e.detail?.item || e.detail || null;

    /* Product type gate: only show for Ring-* or cpb_ordered products */
    if (!isEligibleProductType(item)) return;

    lastItem = item;

    /* Debounce: clear any pending timer (rapid double-add) */
    if (pendingTimer) clearTimeout(pendingTimer);

    pendingTimer = setTimeout(function () {
      pendingTimer = null;
      panelShownThisLoad = true;
      populateCard(lastItem);
      /* Reset to form state in case panel was shown + succeeded earlier in session */
      resetToFormState();
      openPanel();
    }, DELAY_MS);
  });

  /* ── Close actions ── */
  closeBtn?.addEventListener('click', closePanel);
  backdrop?.addEventListener('click', closePanel);
  successClose?.addEventListener('click', closePanel);

  /* "No thanks — continue to checkout" — sets cooldown then follows the link */
  const checkoutLink = document.getElementById('rm-smr-checkout');
  checkoutLink?.addEventListener('click', function () {
    setCooldown();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && overlay.classList.contains('is-open')) closePanel();
  });

  /* ── Form submit ── */
  form?.addEventListener('submit', function (e) {
    e.preventDefault();
    var email = emailInput?.value?.trim() || '';

    /* Validate */
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showError('Please enter a valid email address.');
      emailInput?.classList.add('is-error');
      emailInput?.focus();
      return;
    }

    clearError();
    emailInput?.classList.remove('is-error');
    setSubmitting(true);

    subscribeToKlaviyo(email)
      .catch(function (err) {
        console.error('[SMR] Klaviyo subscribe failed:', err);
        /* Fail silently to UX — still show success, don't penalise the visitor */
      })
      .finally(function () {
        setCooldown();
        /* ── Lead event: GA4, Meta, Pinterest, dataLayer ── */
        if (window.rmTrack) {
          window.rmTrack.lead('Save My Ring', {
            product: lastItem ? (lastItem.product_title || lastItem.title || '') : '',
            page:    window.location.pathname
          });
        }
        showSuccess();
      });
  });

  /* Clear error on typing */
  emailInput?.addEventListener('input', function () {
    emailInput.classList.remove('is-error');
    clearError();
  });

  /* ── Klaviyo Subscribe v3 ── */
  async function subscribeToKlaviyo(email) {
    if (!KLAVIYO_KEY || !KLAVIYO_LIST) {
      console.warn('[SMR] Klaviyo key or list ID not configured.');
      return;
    }

    /* Custom properties — nested under properties, separate from the top-level email */
    var customProps = {};
    if (lastItem) {
      customProps['SMR Product']     = lastItem.product_title || lastItem.title || '';
      customProps['SMR Variant']     = (lastItem.variant_title && lastItem.variant_title !== 'Default Title')
                                        ? lastItem.variant_title : '';
      customProps['SMR Product URL'] = window.location.href;
      customProps['SMR Price']       = lastItem.price ? new Intl.NumberFormat('en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(lastItem.price / 100) : '';
      if (lastImageUrl) customProps['SMR Image URL'] = lastImageUrl;
    }

    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Save My Ring',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email:      email,
                properties: customProps,
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id:   KLAVIYO_LIST,
            },
          },
        },
      },
    };

    var res = await fetch(
      'https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_KEY,
      {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision':     '2026-04-15',
        },
        body: JSON.stringify(payload),
      }
    );

    /* 202 Accepted = success; 409 = already subscribed (treat as success) */
    if (!res.ok && res.status !== 409) {
      var errBody = {};
      try { errBody = await res.json(); } catch (_) {}
      console.error('[SMR] Klaviyo response:', res.status, errBody);
      throw new Error(errBody?.errors?.[0]?.detail || 'Subscribe request failed (' + res.status + ')');
    }
  }

  /* ── UI helpers ── */
  function setSubmitting(isSubmitting) {
    if (!submitBtn) return;
    submitBtn.disabled = isSubmitting;
    if (isSubmitting) {
      submitBtn.dataset.origText = submitBtn.textContent;
      submitBtn.textContent = 'Saving\u2026';
    } else {
      submitBtn.textContent = submitBtn.dataset.origText || 'Save My Ring';
      delete submitBtn.dataset.origText;
    }
  }

  function showError(msg) {
    if (errorEl) errorEl.textContent = msg;
    setSubmitting(false);
  }

  function clearError() {
    if (errorEl) errorEl.textContent = '';
  }

  function showSuccess() {
    setSubmitting(false);
    if (formState)    formState.style.display = 'none';
    if (successState) {
      successState.hidden = false;
      successState.style.display = '';
    }
    successClose?.focus();
  }

  function resetToFormState() {
    if (formState)    formState.style.display = '';
    if (successState) {
      successState.hidden = true;
      successState.style.display = 'none';
    }
    if (emailInput)   emailInput.value = '';
    clearError();
    emailInput?.classList.remove('is-error');
    setSubmitting(false);
  }

  /* ══════════════════════════════════════════════════
     CART PAGE TRIGGER
     Fires on /cart when a cpb_ordered item is present.
     Same cooldown + populate logic as the ATC flow.
  ══════════════════════════════════════════════════ */
  function initCartPageTrigger() {
    if (window.location.pathname !== '/cart') return;

    /* Don't fire if we already showed it this load or cooldown is active */
    if (panelShownThisLoad) return;
    if (isOnCooldown()) return;

    /* Fetch cart to find a cpb_ordered line item */
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var cpbItem = (cart.items || []).find(function (item) {
          return (item.product_type || '') === 'cpb_ordered';
        });
        if (!cpbItem) return;

        /* Short delay so the cart page has fully rendered before the panel appears */
        setTimeout(function () {
          if (panelShownThisLoad) return; /* guard against double-fire */
          panelShownThisLoad = true;
          lastItem = cpbItem;
          populateCard(cpbItem);
          resetToFormState();
          /* Center the panel on desktop when triggered from the cart page */
          overlay.classList.add('rm-smr-overlay--centered');
          openPanel();
        }, 800);
      })
      .catch(function (err) {
        console.log('[SMR] Cart page fetch error:', err.message);
      });
  }

  /* DOMContentLoaded may have already fired by the time this script runs (deferred load) */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartPageTrigger);
  } else {
    initCartPageTrigger();
  }

})();
